import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { ThemeProvider } from "./context/ThemeContext";
import ErrorBoundary from "./components/ErrorBoundary";
import Navbar from "./components/Navbar";
import Welcome from "./pages/Welcome";
import Feed from "./pages/Feed";
import ClaimDetail from "./pages/ClaimDetail";
import CreateClaim from "./pages/CreateClaim";
import Login from "./pages/Login";
import Register from "./pages/Register";
import UserProfile from "./pages/UserProfile";
import ProfileSettings from "./pages/ProfileSettings";
import Notifications from "./pages/Notifications";
import AdminClientLogs from "./pages/AdminClientLogs";
import { getApiData } from "./lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AppRoutes({ user, loading, handleLogin, handleUserUpdate, handleLogout }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Welcome page without navbar */}
      <Route path="/" element={<Welcome />} />
      
      {/* Pages with navbar - each wrapped in its own ErrorBoundary */}
      <Route path="/feed" element={<><Navbar user={user} onLogout={handleLogout} /><ErrorBoundary><Feed user={user} /></ErrorBoundary></>} />
      <Route path="/posts/:postId" element={<><Navbar user={user} onLogout={handleLogout} /><ErrorBoundary><ClaimDetail user={user} /></ErrorBoundary></>} />
      <Route path="/create-post" element={<><Navbar user={user} onLogout={handleLogout} /><ErrorBoundary><CreateClaim user={user} /></ErrorBoundary></>} />
      <Route path="/claims/:claimId" element={<><Navbar user={user} onLogout={handleLogout} /><ErrorBoundary><ClaimDetail user={user} /></ErrorBoundary></>} />
      <Route path="/create-claim" element={<><Navbar user={user} onLogout={handleLogout} /><ErrorBoundary><CreateClaim user={user} /></ErrorBoundary></>} />
      <Route path="/login" element={<ErrorBoundary><Login onLogin={handleLogin} /></ErrorBoundary>} />
      <Route path="/register" element={<ErrorBoundary><Register onLogin={handleLogin} /></ErrorBoundary>} />
      <Route path="/profile/:userId" element={<><Navbar user={user} onLogout={handleLogout} /><ErrorBoundary><UserProfile currentUser={user} onLogout={handleLogout} /></ErrorBoundary></>} />
      <Route path="/settings" element={<><Navbar user={user} onLogout={handleLogout} /><ErrorBoundary><ProfileSettings user={user} onUserUpdate={handleUserUpdate} onLogout={handleLogout} /></ErrorBoundary></>} />
      <Route path="/notifications" element={<><Navbar user={user} onLogout={handleLogout} /><ErrorBoundary><Notifications /></ErrorBoundary></>} />
      <Route path="/admin/logs" element={<><Navbar user={user} onLogout={handleLogout} /><ErrorBoundary><AdminClientLogs /></ErrorBoundary></>} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { standard: true }
      });
      const data = getApiData(response);
      setUser(data);
      localStorage.setItem('user', JSON.stringify(data));
    } catch (err) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleUserUpdate = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <div className="App min-h-screen bg-background">
          <BrowserRouter>
            <AppRoutesWrapper user={user} loading={loading} handleLogin={handleLogin} handleUserUpdate={handleUserUpdate} />
            <Toaster position="top-center" />
          </BrowserRouter>
        </div>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

function AppRoutesWrapper({ user, loading, handleLogin, handleUserUpdate }) {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  return <AppRoutes user={user} loading={loading} handleLogin={handleLogin} handleUserUpdate={handleUserUpdate} handleLogout={handleLogout} />;
}

export default App;
